/**
 * 寫入邊界白名單(票 api-contract-alignment-20260907/01〜03 → 票 40 補齊;ADR-0004)。
 *
 * 來源:`docs/api/backend_v1.00_swagger.json`(2026-09-14 自 SIT 下載)—— 所有 DTO 都是
 * `additionalProperties: false`,Create／Update 的 body 就是同一個 DTO。前端的 view model 帶著
 * 一堆契約外的過渡欄位(IP 分頁、兩段式上架、替代機、結束攤提日…),那些在 mock 模式下要留著
 * (mock 靠它們把 WF 的功能做出來),但**不得**出現在真後端的 payload 裡。
 *
 * 所以白名單不套在 contract 層(hardwareToDto 照舊什麼都給),套在 API 邊界
 * (`createBackendResourceApi` 的 create／update 以及子端點的 outboundFor):請求會打到
 * 真後端時(hybrid 或全真後端)才投影;全 mock 時原樣送。這份清單與 swagger 的一致性由
 * `helpers/__tests__/dtoWhitelist.test.js` 直接讀 swagger 檔守著 —— 後端換版先更新 swagger 檔,
 * 測試紅了再改這裡,不憑記憶改。
 *
 * 清單內容由腳本自 swagger 產生(見該測試檔頂端),請勿手改順序以外的東西。
 */

export const DTO_FIELDS = {
  DcHardwareDto: [
    "business_category_id", "business_category_name", "is_key_business", "property_no",
    "manager_customer_id", "manager_customer_name", "host_name", "asset_category_id",
    "asset_category_name", "asset_no", "brand_id", "brand",
    "model_id", "model", "hardware_no", "purchase_date",
    "purchase_amount", "currency_code", "mounted_date", "mac_address",
    "room_id", "rack_id", "position_u", "active",
    "created_on", "updated_on", "interface_count", "firmware_version",
    "occupied_u", "power_count", "cpu_core_count", "ram_gb",
    "disk_capacity_tb", "operating_system_ids", "operating_systems", "remark",
    "eos_date", "eol_date", "end_of_sale_date", "latest_version", "cpe",
    "cve_date", "kev_date", "vulnerability_hits", "vulnerability_scan_date",
    "vulnerability_scan_file_url", "next_vulnerability_scan_date", "virtual_brand", "virtual_machine_count",
    "virtual_cpu_core_count", "virtual_ram_gb", "virtual_disk_capacity_tb", "application_system_ids",
    "application_systems", "id",
  ],
  DcRackDto: [
    "room_id", "floor_id", "area_id", "business_category_id",
    "business_category_name", "is_key_business", "manager_customer_id", "manager_customer_name",
    "asset_name", "asset_category_id", "asset_category_name", "asset_no",
    "brand", "brand_id", "model", "model_id",
    "rack_no", "power_circuit", "voltage", "capacity_kva",
    "pdu_count", "used_pdu_count", "left_power_count", "right_power_count",
    "total_u", "used_u", "mounted_date", "purchase_amount",
    "currency_code", "description", "active", "active_name",
    "created_on", "updated_on", "id",
  ],
  // 2026-09-14 swagger(SIT＝UAT)新增(票 9月交付/16)。asset_mappings 在 DTO 裡但是唯讀
  // (寫入走 Add／RemoveAssetMapping 子端點);ipToDto 不送它,留在白名單只因 swagger 有這個鍵。
  DcIpAddressDto: [
    "ip_address", "parent_ip_address", "ip_port", "subnet_mask",
    "default_gateway", "dns_server", "mac_address", "physical_port",
    "manager_customer_id", "manager_customer_name", "data_source", "pending_review",
    "last_probed_on", "is_under_review", "approval_request_no", "asset_mappings",
    "created_on", "updated_on", "id",
  ],
  DcSoftwareDto: [
    "business_category_id", "business_category_name", "is_key_business", "property_no",
    "manager_customer_id", "manager_customer_name", "host_name", "asset_category_id",
    "asset_category_name", "asset_no", "brand_id", "brand",
    "model_id", "model", "software_no", "version",
    "environment_id", "environment_name", "remark", "vendor_name",
    "deployed_date", "purchase_amount", "currency_code", "active",
    "created_on", "updated_on", "eos_date", "eol_date",
    "end_of_sale_date", "latest_version", "cpe", "cve_date", "kev_date",
    "vulnerability_hits", "vulnerability_scan_date", "vulnerability_scan_file_url", "next_vulnerability_scan_date",
    "penetration_test_date", "penetration_test_file_url", "next_penetration_test_date", "id",
  ],

  // ── 票 40 補齊：以下 DTO 全部 additionalProperties:false ──

  CustomerRoleDto: [
    "name", "description", "active", "is_system_role",
    "system_name", "role_type_name", "bound_customer_count", "last_modified_on",
    "last_modified_by_customer_id", "last_modified_by_name", "is_under_review", "approval_request_no",
    "modules", "fields", "id",
  ],
  DcCustomerDto: [
    "username", "employee_no", "name", "email",
    "phone_area_code", "phone", "extension", "mobile_country_code",
    "mobile_phone", "password", "confirm_password", "unit_name",
    "job_title", "role_id", "role_name", "active",
    "id",
  ],
  DcRoomDto: [
    "name_chinese", "name_english", "address", "manager_customer_id",
    "manager_customer_name", "active", "active_name", "created_on",
    "updated_on", "id",
  ],
  DcFloorDto: [
    "room_id", "name", "display_order", "active",
    "id",
  ],
  DcAreaDto: [
    "floor_id", "name", "display_order", "active",
    "id",
  ],
  DcContractDto: [
    "contract_no", "name", "manager_customer_id", "manager_customer_name",
    "start_date", "end_date", "year_limit", "service_level_id",
    "service_level_name", "maintenance_cycle_id", "maintenance_cycle_name", "response_hours",
    "on_site_hours", "completion_hours", "vendor_id", "vendor_name",
    "contact_person", "contact_phone", "contact_country_code", "contact_area_code",
    "contact_extension", "contact_email", "amount", "amount_text",
    "currency_code", "description", "primary_related_contract_id", "primary_related_contract_name",
    "is_under_review", "approval_request_no", "created_on_utc", "updated_on_utc",
    "id",
  ],
  DcContractMaintenanceRecordDto: [
    "contract_id", "sequence", "scheduled_on", "status_id",
    "status_name", "attachment_download_ids", "attachments", "reminder14_days",
    "reminder7_days", "reminder1_day", "description", "created_on",
    "id",
  ],
  ChangePasswordModelDto: [
    "old_password", "new_password", "confirm_new_password", "password_must_be_changed",
    "password_expired", "custom_properties",
  ],
  PasswordRecoveryModelDto: [
    "email", "result", "display_captcha", "custom_properties",
  ],
  PasswordRecoveryConfirmModelDto: [
    "new_password", "confirm_new_password", "disable_password_changing", "result",
    "return_url", "custom_properties",
  ],
};

const warned = new Set();

/**
 * 只留契約有的鍵。丟掉的鍵每個資源吵一次(console.warn,dev 用;畫面上不提示 —— 那是 spec 的裁示)。
 * `undefined` 的鍵也不送:JSON 會把它略過,但留著會讓 additionalProperties 的判定看起來模稜兩可。
 */
export function projectToDto(dtoName, payload) {
  const allowed = DTO_FIELDS[dtoName];
  if (!allowed || !payload || typeof payload !== "object") return payload;
  const keep = new Set(allowed);
  const out = {};
  const dropped = [];
  Object.keys(payload).forEach((k) => {
    if (!keep.has(k)) { dropped.push(k); return; }
    if (payload[k] !== undefined) out[k] = payload[k];
  });
  if (dropped.length && !warned.has(dtoName)) {
    warned.add(dtoName);
    console.warn(
      `[contract] ${dtoName} payload 有 ${dropped.length} 個契約外欄位不會送到真後端(additionalProperties:false):`,
      dropped.join(", "),
      "—— 這些是 mock-only 過渡欄位(IP 分頁／兩段式上架／替代機／結束攤提日…),畫面照常填、真後端不會存。"
    );
  }
  return out;
}

/**
 * 什麼時候投影:請求會打到真後端的時候 —— hybrid(已串接的 /api-backend/* 放行到真後端)
 * 或全真後端(USE_MOCK=false)。全 mock 時不投影,mock 才收得到那些過渡欄位。
 * 讀成函式(不是模組常數):測試要在同一個檔案裡涵蓋兩條路。
 */
export const shouldProjectPayload = () =>
  process.env.REACT_APP_USE_MOCK === "hybrid" || process.env.REACT_APP_USE_MOCK === "false";
